'use client';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Accordion from '@/components/ui/Accordion';
import Tabs from '@/components/ui/Tabs';
import Footer from '@/components/common/Footer';
import StickyCTA from '@/components/ui/StickyCTA';

export default function AutoPilotLander() {
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));

    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 800);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      observer?.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const faqItems = [
    {
      id: 'faq-1',
      title: 'What\'s the difference between AutoPilot and concierge?',
      content: 'AutoPilot gives you our systems, templates, and guidance—you execute. Concierge (Execution Coverage, Growth Partner) means we do the work for you. AutoPilot is perfect for hands-on founders who want to learn.'
    },
    {
      id: 'faq-2',
      title: 'Can I upgrade to concierge later?',
      content: 'Yes! Most clients start with AutoPilot to learn our process, then upgrade to Execution Coverage when they want to hand off execution.'
    },
    {
      id: 'faq-3',
      title: 'How much time will I need to spend?',
      content: 'Depends on your plan. Lite clients typically spend 5-10h/week executing. Growth clients spend 10-15h/week. Pro clients get up to 5 hours of hands-on help per month, so they spend less time.'
    },
    {
      id: 'faq-4',
      title: 'What if I get stuck?',
      content: 'All AutoPilot plans include Slack support. Lite gets monthly calls, Growth gets bi-weekly calls, Pro gets weekly calls plus up to 5 hours of hands-on help per month.'
    }
  ];

  const liteFeatures = (
    <div className="space-y-4">
      <div className="p-6 border border-border bg-surface-1">
        <h4 className="text-h5 mb-3">What You Get</h4>
        <ul className="space-y-3 text-body-sm">
          <li className="flex items-start gap-3">
            <span className="text-primary">✓</span>
            <span>Access to templates library (ads, emails, landing pages, social posts)</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-primary">✓</span>
            <span>Monthly strategy call (30 min)</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-primary">✓</span>
            <span>Async Slack support (48h response time)</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-primary">✓</span>
            <span>Campaign review & feedback</span>
          </li>
        </ul>
      </div>
      <div className="p-6 border border-border bg-surface-1">
        <h4 className="text-h5 mb-3">Best For</h4>
        <p className="text-body-sm text-muted-foreground">
          Solo founders and bootstrapped startups who want templates and guidance but will execute themselves.
        </p>
      </div>
    </div>
  );

  const growthFeatures = (
    <div className="space-y-4">
      <div className="p-6 border border-border bg-surface-1">
        <h4 className="text-h5 mb-3">What You Get</h4>
        <ul className="space-y-3 text-body-sm">
          <li className="flex items-start gap-3">
            <span className="text-primary">✓</span>
            <span>Everything in Lite</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-primary">✓</span>
            <span>Bi-weekly strategy calls (45 min)</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-primary">✓</span>
            <span>Priority Slack support (24h response time)</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-primary">✓</span>
            <span>Monthly performance review</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-primary">✓</span>
            <span>Access to modules library</span>
          </li>
        </ul>
      </div>
      <div className="p-6 border border-border bg-surface-1">
        <h4 className="text-h5 mb-3">Best For</h4>
        <p className="text-body-sm text-muted-foreground">
          Growing teams who want more support, more templates, and regular check-ins to stay on track.
        </p>
      </div>
    </div>
  );

  const proFeatures = (
    <div className="space-y-4">
      <div className="p-6 border border-border bg-surface-1">
        <h4 className="text-h5 mb-3">What You Get</h4>
        <ul className="space-y-3 text-body-sm">
          <li className="flex items-start gap-3">
            <span className="text-primary">✓</span>
            <span>Everything in Growth</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-primary">✓</span>
            <span>Weekly strategy calls (60 min)</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-primary">✓</span>
            <span>Up to 5 hours hands-on help per month</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-primary">✓</span>
            <span>Campaign setup assistance</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-primary">✓</span>
            <span>Quarterly roadmap planning</span>
          </li>
        </ul>
      </div>
      <div className="p-6 border border-border bg-surface-1">
        <h4 className="text-h5 mb-3">Best For</h4>
        <p className="text-body-sm text-muted-foreground">
          Teams who want maximum support without full concierge. You execute, but we help with setup and troubleshooting.
        </p>
      </div>
    </div>
  );

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <AppImage 
              src="/assets/images/boc_logo_240x90_white-1771394304209.png"
              alt="Best Odds Corp Logo"
              width={180}
              height={68}
              className="cursor-pointer"
            />
          </Link>
          <Link href="/cart">
            <Button variant="primary" size="md" className="border-glow">
              Get Started - From $499/mo
            </Button>
          </Link>
        </div>
      </nav>

      {showStickyCTA && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-md border-t border-border py-4 px-6">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div>
              <p className="text-h5">AutoPilot DIY</p>
              <p className="text-body-sm text-muted-foreground">From $499/mo • You execute, we guide</p>
            </div>
            <Link href="/cart">
              <Button variant="primary" size="lg" className="border-glow">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      )}

      <StickyCTA
        title="AutoPilot DIY"
        subtitle="From $499/mo • You execute, we guide"
        ctaText="Get Started"
        ctaHref="/cart"
        showAfterScroll={800}
      />

      <main className="min-h-screen bg-background text-foreground pt-24">
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto text-center">
            <div className="reveal">
              <Badge variant="primary" className="mb-6">DIY with Expert Guidance</Badge>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
                Our systems,
                <br />
                <span className="text-primary">your execution</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-12 leading-relaxed">
                Templates, strategy calls, and async support. Perfect for hands-on founders who want to learn and execute.
              </p>
              <Link href="/cart">
                <Button variant="primary" size="lg" className="border-glow-strong">
                  Start AutoPilot - From $499/mo
                </Button>
              </Link>
              <p className="text-body-sm text-muted-foreground mt-4">
                Cancel anytime. No contracts.
              </p>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="reveal p-8 border border-primary bg-surface-1">
                <h3 className="text-h3 mb-6">This is for you if:</h3>
                <ul className="space-y-4 text-body-sm">
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You want to learn marketing, not just outsource it</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You have time to execute but need templates and guidance</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You're bootstrapped and want to maximize ROI</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You want to test our process before committing to concierge</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You're building an in-house team and need systems</span>
                  </li>
                </ul>
              </div>
              <div className="reveal p-8 border border-border bg-surface-1">
                <h3 className="text-h3 mb-6">This is NOT for you if:</h3>
                <ul className="space-y-4 text-body-sm text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You want us to do the work (go with Execution Coverage)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You don't have 5-15 hours per week to execute</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You need immediate results (DIY takes time to learn)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You're looking for a course or training program (this is ongoing support)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Choose Your Plan
              </span>
              <h2 className="text-h2 mb-4">Three Tiers, One System</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              <div className="reveal p-8 border border-border bg-background">
                <h3 className="text-h4 mb-2">Lite</h3>
                <div className="mb-6">
                  <span className="text-h2 text-primary">$499</span>
                  <span className="text-body text-muted-foreground">/month</span>
                </div>
                <p className="text-body-sm text-muted-foreground mb-6">
                  Templates and monthly strategy calls.
                </p>
                <Link href="/cart" className="block w-full text-center px-6 py-3 text-label uppercase tracking-widest font-semibold transition-all bg-transparent text-foreground border border-border hover:border-primary hover:text-primary">
                  Get Started
                </Link>
              </div>
              <div className="reveal p-8 border border-primary bg-surface-2">
                <Badge variant="primary" className="mb-4">Most Popular</Badge>
                <h3 className="text-h4 mb-2">Growth</h3>
                <div className="mb-6">
                  <span className="text-h2 text-primary">$999</span>
                  <span className="text-body text-muted-foreground">/month</span>
                </div>
                <p className="text-body-sm text-muted-foreground mb-6">
                  More support, bi-weekly calls, modules library.
                </p>
                <Link href="/cart" className="block w-full text-center px-6 py-3 text-label uppercase tracking-widest font-semibold transition-all bg-primary text-primary-foreground hover:bg-primary-hover border border-primary hover:shadow-glow">
                  Get Started
                </Link>
              </div>
              <div className="reveal p-8 border border-border bg-background">
                <h3 className="text-h4 mb-2">Pro</h3>
                <div className="mb-6">
                  <span className="text-h2 text-primary">$1,500</span>
                  <span className="text-body text-muted-foreground">/month</span>
                </div>
                <p className="text-body-sm text-muted-foreground mb-6">
                  Weekly calls + 5 hours hands-on help.
                </p>
                <Link href="/cart" className="block w-full text-center px-6 py-3 text-label uppercase tracking-widest font-semibold transition-all bg-primary text-primary-foreground hover:bg-primary-hover">
                  Get Started
                </Link>
              </div>
            </div>
            <div className="reveal">
              <Tabs
                tabs={[
                  { id: 'lite', label: 'Lite', content: liteFeatures },
                  { id: 'growth', label: 'Growth', content: growthFeatures },
                  { id: 'pro', label: 'Pro', content: proFeatures }
                ]}
                defaultTab="growth"
              />
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                What You Get
              </span>
              <h2 className="text-h2 mb-4">Templates & Systems</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="reveal p-6 border border-border bg-surface-1">
                <h4 className="text-h5 mb-3">Ad Templates</h4>
                <p className="text-body-sm text-muted-foreground">
                  Google, Meta, LinkedIn ad templates with proven copy frameworks.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-surface-1">
                <h4 className="text-h5 mb-3">Email Sequences</h4>
                <p className="text-body-sm text-muted-foreground">
                  Welcome series, nurture campaigns, and promotional email templates.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-surface-1">
                <h4 className="text-h5 mb-3">Landing Pages</h4>
                <p className="text-body-sm text-muted-foreground">
                  High-converting landing page templates for lead gen and sales.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-surface-1">
                <h4 className="text-h5 mb-3">Social Posts</h4>
                <p className="text-body-sm text-muted-foreground">
                  Monthly content calendars with captions and creative direction.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-surface-1">
                <h4 className="text-h5 mb-3">Campaign Briefs</h4>
                <p className="text-body-sm text-muted-foreground">
                  Step-by-step briefs for launches, promotions, and campaigns.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-surface-1">
                <h4 className="text-h5 mb-3">Tracking Setup</h4>
                <p className="text-body-sm text-muted-foreground">
                  Analytics setup guides and dashboard templates.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-surface-1">
                <h4 className="text-h5 mb-3">SOPs & Checklists</h4>
                <p className="text-body-sm text-muted-foreground">
                  Standard operating procedures for every marketing task.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-surface-1">
                <h4 className="text-h5 mb-3">Strategy Frameworks</h4>
                <p className="text-body-sm text-muted-foreground">
                  Capacity planning, channel prioritization, and roadmap templates.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Timeline
              </span>
              <h2 className="text-h2 mb-4">First 90 Days</h2>
            </div>
            <div className="space-y-8">
              <div className="reveal flex gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 border border-primary flex items-center justify-center">
                  <span className="text-primary text-h4 font-bold">30</span>
                </div>
                <div>
                  <h4 className="text-h5 mb-2">Days 1-30: Learn the System</h4>
                  <p className="text-body-sm text-muted-foreground">
                    Onboarding call, access to templates, first strategy session, and start executing your first campaign.
                  </p>
                </div>
              </div>
              <div className="reveal flex gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 border border-primary flex items-center justify-center">
                  <span className="text-primary text-h4 font-bold">60</span>
                </div>
                <div>
                  <h4 className="text-h5 mb-2">Days 31-60: Execute & Optimize</h4>
                  <p className="text-body-sm text-muted-foreground">
                    Run campaigns, get feedback on strategy calls, optimize based on results.
                  </p>
                </div>
              </div>
              <div className="reveal flex gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 border border-primary flex items-center justify-center">
                  <span className="text-primary text-h4 font-bold">90</span>
                </div>
                <div>
                  <h4 className="text-h5 mb-2">Days 61-90: Scale or Upgrade</h4>
                  <p className="text-body-sm text-muted-foreground">
                    By day 90, you'll know the system. Either scale your DIY execution or upgrade to Execution Coverage.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Proof
              </span>
              <h2 className="text-h2 mb-4">What AutoPilot Clients Say</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="reveal p-8 border border-border bg-surface-1">
                <p className="text-body-sm text-muted-foreground mb-6">
                  "I learned more in 3 months of AutoPilot than I did in 2 years of YouTube tutorials. The templates are gold."
                </p>
                <p className="text-body-sm font-semibold">— Alex T., Solo Founder</p>
              </div>
              <div className="reveal p-8 border border-border bg-surface-1">
                <p className="text-body-sm text-muted-foreground mb-6">
                  "Started with AutoPilot Growth, upgraded to Execution Coverage after 6 months. Perfect onboarding path."
                </p>
                <p className="text-body-sm font-semibold">— Priya K., Startup</p>
              </div>
              <div className="reveal p-8 border border-border bg-surface-1">
                <p className="text-body-sm text-muted-foreground mb-6">
                  "The strategy calls are worth the price alone. I finally have a clear plan and the templates to execute it."
                </p>
                <p className="text-body-sm font-semibold">— Jordan L., E-commerce</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Questions?
              </span>
              <h2 className="text-h2 mb-4">Common Questions</h2>
            </div>
            <div className="max-w-3xl mx-auto reveal">
              <Accordion items={faqItems} allowMultiple={true} />
            </div>
          </div>
        </section>

        <section className="py-24 px-6">
          <div className="max-w-[1200px] mx-auto text-center">
            <div className="reveal">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Start learning today.
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                From $499/mo. Cancel anytime. No contracts.
              </p>
              <Link href="/cart">
                <Button variant="primary" size="lg" className="border-glow-strong">
                  Get Started with AutoPilot
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Upgrade Path Section */}
        <section className="py-24 px-6 border-t border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-12">
              <h2 className="text-3xl font-bold mb-4">Ready to Hand Off Execution?</h2>
              <p className="text-body text-muted-foreground">Upgrade to concierge when you're ready</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link href="/lp/execution-coverage" className="reveal p-6 border border-border hover:border-primary transition-colors group">
                <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">Execution Coverage</h3>
                <p className="text-body-sm text-muted-foreground">We execute, you approve</p>
              </Link>
              <Link href="/lp/growth-partner" className="reveal p-6 border border-border hover:border-primary transition-colors group">
                <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">Growth Partner</h3>
                <p className="text-body-sm text-muted-foreground">Premium execution and priority</p>
              </Link>
              <Link href="/pricing" className="reveal p-6 border border-border hover:border-primary transition-colors group">
                <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">Compare All Plans</h3>
                <p className="text-body-sm text-muted-foreground">See full pricing breakdown</p>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}